import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { 
  Package, CheckCircle, Clock, Truck, RotateCcw, ClipboardCheck, 
  AlertCircle, Loader2, Calendar, MapPin, User, Mail, Phone, ArrowLeft, Home
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  received: { icon: Package, color: 'text-blue-400', bg: 'bg-blue-500', label: 'Order Received' },
  processing: { icon: Clock, color: 'text-yellow-400', bg: 'bg-yellow-500', label: 'Processing/Filling' },
  ready_for_pickup: { icon: CheckCircle, color: 'text-green-400', bg: 'bg-green-500', label: 'Ready for Pickup' },
  delivered: { icon: Truck, color: 'text-purple-400', bg: 'bg-purple-500', label: 'Delivered to Instructor' },
  returned: { icon: RotateCcw, color: 'text-orange-400', bg: 'bg-orange-500', label: 'Returned to TS' },
  inspected: { icon: ClipboardCheck, color: 'text-teal-400', bg: 'bg-teal-500', label: 'Inspected' },
  complete: { icon: CheckCircle, color: 'text-emerald-400', bg: 'bg-emerald-500', label: 'Complete' },
  cancelled: { icon: AlertCircle, color: 'text-red-400', bg: 'bg-red-500', label: 'Cancelled' }
};

const STATUS_ORDER = ['received', 'processing', 'ready_for_pickup', 'delivered', 'returned', 'inspected', 'complete'];

const RentalTracking = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const fetchOrder = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/rentals/track/${orderId}`);
        setOrder(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching order:', err);
        setError(err.response?.data?.detail || 'Order not found');
      } finally {
        setLoading(false);
      }
    };
    
    if (orderId) {
      fetchOrder();
    }
  }, [orderId]);
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-purple-400" />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-slate-800/90 border-red-500/30 p-8 text-center">
          <AlertCircle className="h-16 w-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Order Not Found</h1>
          <p className="text-slate-400 mb-6">{error}</p>
          <div className="flex gap-3">
            <Button 
              variant="outline"
              onClick={() => window.location.href = '/'}
              className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
              data-testid="back-to-booking-btn"
            >
              <Home className="h-4 w-4 mr-2" />
              Book a Class
            </Button>
            <Button 
              variant="outline"
              onClick={() => window.location.href = '/rentals'}
              className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
              data-testid="back-to-rentals-btn"
            >
              <Package className="h-4 w-4 mr-2" />
              Rent Equipment
            </Button>
          </div>
        </Card>
      </div>
    );
  }
  
  const currentStatusIndex = STATUS_ORDER.indexOf(order?.status);
  const currentConfig = STATUS_CONFIG[order?.status] || STATUS_CONFIG.received;
  const CurrentIcon = currentConfig.icon;
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return dateStr;
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4 md:p-6">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Order Tracking</h1>
          <p className="text-slate-400 font-mono mb-4">{order?.order_id}</p>
          <div className="flex justify-center gap-3">
            <Button 
              variant="ghost"
              size="sm"
              onClick={() => window.location.href = '/'}
              className="text-slate-400 hover:text-white hover:bg-slate-700"
              data-testid="back-to-booking-btn"
            >
              <Home className="h-4 w-4 mr-2" />
              Book a Class
            </Button>
            <Button 
              variant="ghost"
              size="sm"
              onClick={() => window.location.href = '/rentals'}
              className="text-slate-400 hover:text-white hover:bg-slate-700"
              data-testid="back-to-rentals-btn"
            >
              <Package className="h-4 w-4 mr-2" />
              Rent Equipment
            </Button>
          </div>
        </div>
        
        {/* Current Status */}
        <Card className="bg-slate-800/80 border-purple-500/30 p-6 mb-6">
          <div className="flex items-center justify-center gap-4">
            <div className={`p-4 rounded-full ${currentConfig.bg}/20`}>
              <CurrentIcon className={`h-10 w-10 ${currentConfig.color}`} />
            </div>
            <div>
              <p className="text-slate-400 text-sm">Current Status</p>
              <p className={`text-2xl font-bold ${currentConfig.color}`}>
                {order?.status_label || currentConfig.label}
              </p>
            </div>
          </div>
        </Card>
        
        {/* Status Timeline */}
        <Card className="bg-slate-800/80 border-slate-600/30 p-6 mb-6">
          <h2 className="text-lg font-semibold text-white mb-6">Order Progress</h2>
          
          <div className="relative">
            {/* Progress Line */}
            <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-slate-700"></div>
            <div 
              className="absolute left-6 top-0 w-0.5 bg-purple-500 transition-all duration-500"
              style={{ 
                height: `${Math.min(100, (currentStatusIndex / (STATUS_ORDER.length - 1)) * 100)}%` 
              }}
            ></div>
            
            {/* Status Steps */}
            <div className="space-y-6">
              {STATUS_ORDER.map((status, index) => {
                const config = STATUS_CONFIG[status];
                const Icon = config.icon;
                const isComplete = index < currentStatusIndex;
                const isCurrent = index === currentStatusIndex;
                const timestamp = order?.status_history?.[status];
                
                return (
                  <div key={status} className="flex items-start gap-4 relative">
                    <div className={`
                      w-12 h-12 rounded-full flex items-center justify-center z-10
                      ${isComplete ? config.bg : isCurrent ? `${config.bg}/50 ring-2 ring-${config.color}` : 'bg-slate-700'}
                    `}>
                      <Icon className={`h-5 w-5 ${isComplete || isCurrent ? 'text-white' : 'text-slate-500'}`} />
                    </div>
                    <div className="flex-1 pt-2">
                      <p className={`font-medium ${isComplete || isCurrent ? 'text-white' : 'text-slate-500'}`}>
                        {config.label}
                      </p>
                      {timestamp && (
                        <p className="text-xs text-slate-400 mt-1">
                          {formatDate(timestamp)}
                        </p>
                      )}
                    </div>
                    {isCurrent && (
                      <span className="px-2 py-1 bg-purple-600 text-white text-xs rounded-full">
                        Current
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </Card>
        
        {/* Order Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Items */}
          <Card className="bg-slate-800/80 border-slate-600/30 p-4">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Package className="h-5 w-5 text-purple-400" />
              Items
            </h3>
            <div className="space-y-2">
              {order?.items?.map((item, index) => (
                <div key={index} className="flex justify-between items-center bg-slate-700/50 rounded-lg p-2">
                  <span className="text-slate-300 text-sm">{item.name}</span>
                  <span className="text-white font-medium">×{item.quantity}</span>
                </div>
              ))}
            </div>
          </Card>
          
          {/* Schedule */}
          <Card className="bg-slate-800/80 border-slate-600/30 p-4">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Calendar className="h-5 w-5 text-green-400" />
              Schedule
            </h3>
            <div className="space-y-3">
              <div>
                <p className="text-xs text-slate-400">Pickup</p>
                <p className="text-white">{formatDate(order?.pickup_datetime)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400">Return</p>
                <p className="text-white">{formatDate(order?.return_datetime)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400">Order Placed</p>
                <p className="text-slate-300 text-sm">{formatDate(order?.created_at)}</p>
              </div>
            </div>
          </Card>
        </div>
        
        {/* Help Text */}
        <div className="mt-8 text-center">
          <p className="text-slate-400 text-sm">
            Questions about your order? Contact us for assistance.
          </p>
        </div>
      </div>
    </div>
  );
};

export default RentalTracking;
